/**
 * Cross-platform network status.
 *
 * Web: `navigator.onLine` + `online` / `offline` window events. Good enough
 * as a rough hint — browsers already throttle requests and surface their own
 * offline UI.
 *
 * Native: lazy-imports `@capacitor/network`, which reports both connectivity
 * and the connection type (wifi / cellular / none). We only surface
 * connected vs not to keep the UI simple; `connectionType` is exposed for
 * future per-tier features (e.g. defer large downloads on cellular).
 */

import { isNative } from './index'

export type ConnectionType = 'wifi' | 'cellular' | 'none' | 'unknown'

export interface NetworkStatus {
  connected: boolean
  connectionType: ConnectionType
}

export type Unsubscribe = () => void

function webStatus(): NetworkStatus {
  if (typeof navigator === 'undefined') {
    return { connected: true, connectionType: 'unknown' }
  }
  return { connected: navigator.onLine !== false, connectionType: 'unknown' }
}

export async function getStatus(): Promise<NetworkStatus> {
  if (!isNative()) return webStatus()
  try {
    const mod = await import(/* @vite-ignore */ '@capacitor/network')
    const status = await mod.Network.getStatus()
    return {
      connected: status.connected,
      connectionType: (status.connectionType ?? 'unknown') as ConnectionType,
    }
  } catch {
    return webStatus()
  }
}

export function onChange(callback: (status: NetworkStatus) => void): Unsubscribe {
  if (!isNative()) {
    if (typeof window === 'undefined') return () => {}
    const handler = () => callback(webStatus())
    window.addEventListener('online', handler)
    window.addEventListener('offline', handler)
    return () => {
      window.removeEventListener('online', handler)
      window.removeEventListener('offline', handler)
    }
  }

  let cleanup: Unsubscribe = () => {}
  let disposed = false
  ;(async () => {
    try {
      const mod = await import(/* @vite-ignore */ '@capacitor/network')
      if (disposed) return
      const handle = await mod.Network.addListener('networkStatusChange', (status) => {
        callback({
          connected: status.connected,
          connectionType: (status.connectionType ?? 'unknown') as ConnectionType,
        })
      })
      cleanup = () => { void handle.remove() }
    } catch {
      /* plugin missing — leave cleanup as no-op */
    }
  })()

  return () => {
    disposed = true
    cleanup()
  }
}
